import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Sparkles, ShoppingBag, Wallet, Smartphone, Tv2, UtensilsCrossed, Home, HeartPulse, BookOpen, ArrowUpRight } from "lucide-react";
import { IMG, TRUSTED_BY } from "@/lib/images";
import PageBanner from "@/components/common/PageBanner";

const INDUSTRIES = [
  { icon: Car, name: "Automotive", body: "Atrium car displays, kiosks and HD LED takeovers driving test-drive intent in Tier-1 metros." },
  { icon: Sparkles, name: "FMCG / Beauty", body: "Sampling, trial booths and synchronised DOOH for festive and launch windows." },
  { icon: ShoppingBag, name: "Fashion & Luxury", body: "Cinematic creative on HNI-grade malls — Palladium, UB City, Quest, Select Citywalk." },
  { icon: Wallet, name: "BFSI", body: "Kiosk-led acquisition for credit cards, loans and insurance in premium catchments." },
  { icon: Smartphone, name: "Telecom", body: "Multi-format domination for plan launches, 5G rollouts and brand campaigns." },
  { icon: Tv2, name: "OTT / Entertainment", body: "Series launches with cinematic mall takeovers across India." },
  { icon: UtensilsCrossed, name: "F&B / QSR", body: "Food-court domination + sampling for new menu launches and flash promotions." },
  { icon: Home, name: "Real Estate", body: "HNI-targeted LED + atrium showcases in upper-affluent micro-markets." },
  { icon: HeartPulse, name: "Healthcare", body: "Diagnostics, pharma OTC and insurance — sampling-led trust campaigns." },
  { icon: BookOpen, name: "Education", body: "Counsellor activations and brand awareness in education-buyer catchments." },
];

export default function Industries() {
  return (
    <div data-testid="industries-page" className="bg-[var(--bone)]">
      <PageBanner
        eyebrow="Industries"
        title="Every vertical,"
        italicWord="every audience."
        kicker="From luxury automotive to challenger D2C — ten categories already shipping on Fundle."
        image={IMG.backlitPillar}
      />

      {/* Brand logo wall */}
      <section className="border-b border-[var(--border)] py-12 bg-[var(--bone)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--slate-2)] mb-6 text-center">Categories live on Fundle</div>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-10 gap-y-6 items-center justify-items-center">
            {TRUSTED_BY.map((b) => (
              <div key={b} className="font-serif text-xl text-[var(--slate-2)] tracking-[0.18em] hover:text-[var(--ink)] transition-colors">{b}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]">
        {INDUSTRIES.map((i, idx) => (
          <motion.div
            key={i.name}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: idx * 0.04 }}
            className="bg-[var(--bone)] p-8 lg:p-10 group hover:bg-white transition-colors"
            data-testid={`industry-${idx}`}
          >
            <i.icon size={22} className="text-[var(--gold)]" strokeWidth={1.5} />
            <div className="mt-6 font-serif-display text-2xl text-[var(--ink)]">{i.name}</div>
            <p className="mt-3 text-sm text-[var(--slate-1)] leading-relaxed">{i.body}</p>
          </motion.div>
        ))}
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-20 text-center">
        <h2 className="font-serif-display text-4xl lg:text-5xl text-[var(--ink)]">
          Your category is <span className="font-serif-italic text-[var(--gold)]">already mapped.</span>
        </h2>
        <p className="mt-4 text-[var(--slate-1)]">Tell us your brief — we&apos;ll plan the malls.</p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
          <Link to="/ai-planner" className="btn-primary">Plan with AI</Link>
          <Link to="/contact" className="btn-secondary">Talk to us <ArrowUpRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
